import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Phone, X, ArrowRight, Store } from "lucide-react";
import { SiUbereats, SiDoordash } from "react-icons/si";
import { useOrder } from "../../context/OrderContext";
import { ORDER, BRAND } from "../../data/content";
import { BrandName } from "./BrandName";

const platforms = [
  { key: "ubereats", label: "Uber Eats", href: ORDER.ubereats, color: "#06C167", icon: <SiUbereats className="text-2xl" /> },
  { key: "doordash", label: "DoorDash", href: ORDER.doordash, color: "#FF3008", icon: <SiDoordash className="text-2xl" /> },
  { key: "skipthedishes", label: "SkipTheDishes", href: ORDER.skipthedishes, color: "#FF8000", icon: <span className="font-display font-bold text-lg">S</span> },
  { key: "fantuan", label: "Fantuan", href: ORDER.fantuan, color: "#FF5B34", icon: <span className="font-display font-bold text-lg">福</span> },
];

export const OrderModal = () => {
  const { open, closeOrder } = useOrder();
  const navigate = useNavigate();
  const goToPickupMenu = () => { closeOrder(); navigate("/order"); };
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="order-modal"
        >
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={closeOrder}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            className="relative w-full sm:max-w-lg bg-surface border border-gold/20 rounded-t-[28px] sm:rounded-[28px] p-6 sm:p-8 gold-glow"
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 260 }}
          >
            <button
              onClick={closeOrder}
              aria-label="Close"
              data-testid="order-modal-close"
              className="absolute right-4 top-4 h-10 w-10 grid place-items-center rounded-full border border-white/10 text-cream/70 hover:text-cream hover:border-gold/40 transition-colors"
            >
              <X size={18} />
            </button>

            <p className="text-[11px] uppercase tracking-[0.28em] text-gold font-semibold">Order Now</p>
            <h3 className="mt-2 text-2xl sm:text-3xl font-display font-semibold leading-tight">
              Get <BrandName /> your way
            </h3>
            <p className="mt-2 text-sm text-cream/60">Pickup, direct order, or delivery to your door.</p>

            {/* Pickup / direct */}
            <div className="mt-6 grid gap-3">
              <button
                onClick={goToPickupMenu}
                data-testid="order-online-direct"
                className="group flex w-full items-center gap-4 rounded-2xl bg-gold px-5 py-4 text-left text-ink font-semibold transition-transform hover:-translate-y-0.5"
              >
                <Store size={22} />
                <span className="flex-1">
                  Order Online for Pickup
                  <span className="block text-[12px] font-medium text-ink/70">Build your order and check out in seconds</span>
                </span>
                <ArrowRight size={16} className="text-ink/50" />
              </button>
              <a
                href={BRAND.phoneHref}
                data-testid="order-call-pickup"
                className="group flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-4 text-cream font-semibold transition-colors hover:border-gold/40 hover:bg-white/[0.07]"
              >
                <Phone size={22} className="text-gold" />
                <span className="flex-1">
                  Call for Pickup
                  <span className="block text-[12px] font-medium text-cream/50">{BRAND.phoneDisplay} · we'll take your order by phone</span>
                </span>
              </a>
            </div>

            <div className="my-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-cream/40">or get it delivered</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {platforms.map((p) => (
                <a
                  key={p.key}
                  href={p.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`order-${p.key}`}
                  className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3.5 transition-all hover:-translate-y-0.5 hover:bg-white/[0.06]"
                  style={{ boxShadow: "none" }}
                >
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-white"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.icon}
                  </span>
                  <span className="text-sm font-semibold text-cream">{p.label}</span>
                </a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OrderModal;
